import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, DataTable, DataTablePagination, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from '@cargomatic/ui';
import { useLaneRates } from 'hooks/useLaneRate';
import { t } from 'i18next';
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from '@tanstack/react-table';
import useModal from 'hooks/useModal';
import { ArrowRightIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, MoreHorizontal, } from 'lucide-react';
import AuditTrailModal from 'components/modules/pricing/modals/LaneRateAuditTrailModal';
import { LaneRatesCsvUploadModal } from 'components/modules/pricing/LaneRatesCsvUploadModal';
import { getColumns } from './columns';
import { EditRateSequenceModal } from './EditRateSequenceModal';
const highlighted = {
    rateCustomer: true,
    rateMarket: true,
    rateEquipmentType: true,
    rateEquipmentSize: true,
    rateHazardous: true,
    rateOrderType: true,
    rateSteamshipOrRailroad: true,
};
export default function LaneRatesRateConditions() {
    const columns = getColumns();
    const [hideRateConditionColumns, setHideRateConditionColumns] = useState(true);
    const [columnVisibility, setColumnVisibility] = useState({
        rateCustomer: false,
        rateEquipmentType: false,
        rateEquipmentSize: false,
        rateHazardous: false,
        rateOrderType: false,
        rateSteamshipOrRailroad: false,
    });
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageSize: 10,
        pageIndex: 0,
    });
    const { visible, show, close } = useModal();
    const [selectedRateSequence, setSelectedRateSequence] = useState(null);
    const [rowSelection, setRowSelection] = useState({});
    const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
    const [rateAudit, setRateAudit] = useState(null);
    const [showLaneRatesCsvUploadModal, setLaneRatesCsvUploadModalIsOpen] = useState(false);
    const history = useHistory();
    const { data, isLoading } = useLaneRates({ sorting, pagination });
    const selectedRowCount = useMemo(() => {
        return Object.keys(rowSelection).length;
    }, [rowSelection]);
    function handleRowSelectionChange(selection) {
        setRowSelection(selection);
    }
    function handleSortChange(updater) {
        const newValue = updater(sorting);
        setSorting(newValue);
    }
    function onEditRateSequence(laneRate) {
        setSelectedRateSequence(laneRate);
        show();
    }
    // Once we update the edit page to use query strings, update to a link with lane rate ids
    function onEditLaneRate(laneRate) {
        history.push('/lane-rates/edit', {
            laneRates: [
                Object.assign(Object.assign({}, laneRate), { weeklyCommitment: laneRate.commitment }),
            ],
            listQueryFilterParams: {},
        });
    }
    function onBulkEditRateSequence() {
        const selectedLaneRates = Object.keys(rowSelection).map((index) => {
            var _a, _b, _c;
            return Object.assign(Object.assign({}, (_a = data === null || data === void 0 ? void 0 : data.laneRates) === null || _a === void 0 ? void 0 : _a[index]), { weeklyCommitment: (_c = (_b = data === null || data === void 0 ? void 0 : data.laneRates) === null || _b === void 0 ? void 0 : _b[index]) === null || _c === void 0 ? void 0 : _c.commitment });
        });
        history.push('/lane-rates/edit', {
            laneRates: selectedLaneRates,
            listQueryFilterParams: {},
        });
    }
    function handleAuditClick(laneRate) {
        setRateAudit(laneRate);
        setShowAuditTrailModal(true);
    }
    function handleBulkUploadClick() {
        setLaneRatesCsvUploadModalIsOpen(true);
    }
    function renderViewRateCard(laneRate) {
        const requiredDoc = (laneRate === null || laneRate === void 0 ? void 0 : laneRate.document) || (laneRate === null || laneRate === void 0 ? void 0 : laneRate.oldDocument);
        const url = (requiredDoc === null || requiredDoc === void 0 ? void 0 : requiredDoc.signedUrl) || '';
        if (requiredDoc && url) {
            return (React.createElement(DropdownMenuItem, null,
                React.createElement(Link, { to: { pathname: url }, target: "_blank", className: "text-black" }, t('VIEW_RATE_CARD'))));
        }
    }
    const mappedColumnDefs = useMemo(() => {
        return columns.concat({
            id: 'actions',
            cell: ({ row }) => {
                const payment = row.original;
                return (React.createElement("div", { className: "text-right" },
                    React.createElement(DropdownMenu, { modal: false },
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { variant: "ghost", className: "w-8 h-8 p-0" },
                                React.createElement("span", { className: "sr-only" }, t('OPEN_MENU')),
                                React.createElement(MoreHorizontal, { className: "w-4 h-4" }))),
                        React.createElement(DropdownMenuContent, { align: "end" },
                            React.createElement(DropdownMenuLabel, null, t('ACTIONS')),
                            React.createElement(DropdownMenuItem, { onClick: () => onEditRateSequence(payment) }, t('EDIT_RATE_SEQUENCE')),
                            React.createElement(DropdownMenuItem, { onClick: () => onEditLaneRate(payment) }, t('EDIT_LANE_RATE')),
                            React.createElement(DropdownMenuSeparator, null),
                            renderViewRateCard(payment),
                            React.createElement(DropdownMenuItem, { onClick: () => handleAuditClick(payment) }, t('VIEW_AUDIT_TRAIL'))))));
            },
        });
    }, []);
    function handlePaginationChange(selection) {
        setPagination(selection);
    }
    const options = {
        columns: mappedColumnDefs,
        data: (data === null || data === void 0 ? void 0 : data.laneRates) || [],
        enableRowSelection: true,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            columnVisibility,
            sorting,
            pagination,
            rowSelection,
        },
        onColumnVisibilityChange: setColumnVisibility,
        onSortingChange: handleSortChange,
        onPaginationChange: handlePaginationChange,
        onRowSelectionChange: handleRowSelectionChange,
    };
    const table = useReactTable(Object.assign(Object.assign({}, options), { manualSorting: true, manualPagination: true, rowCount: data === null || data === void 0 ? void 0 : data.count }));
    useEffect(() => {
        if (hideRateConditionColumns) {
            setColumnVisibility({
                rateCustomer: false,
                rateEquipmentType: false,
                rateEquipmentSize: false,
                rateHazardous: false,
                rateOrderType: false,
                rateSteamshipOrRailroad: false,
            });
        }
        else {
            setColumnVisibility({
                rateCustomer: true,
                rateEquipmentType: true,
                rateEquipmentSize: true,
                rateHazardous: true,
                rateOrderType: true,
                rateSteamshipOrRailroad: true,
            });
        }
    }, [hideRateConditionColumns]);
    function renderFooter() {
        if (isLoading) {
            return null;
        }
        return React.createElement(DataTablePagination, { table: table });
    }
    function onToggleRateConditionsVisibility() {
        setHideRateConditionColumns((visibility) => !visibility);
    }
    function renderHeader() {
        return (React.createElement(CardHeader, null,
            React.createElement("div", { className: "flex justify-between items-center" },
                React.createElement(CardTitle, null, t('ACCESSORIAL_RATES')),
                React.createElement("div", { className: "flex justify-end" },
                    React.createElement("div", { className: "flex space-x-4" },
                        React.createElement(Button, { onClick: onToggleRateConditionsVisibility, className: [
                                'h-10',
                                {
                                    'bg-slate-50': !hideRateConditionColumns,
                                },
                            ], size: "sm", variant: "outline" },
                            hideRateConditionColumns
                                ? t('EXPAND_RATE_CONDITIONS')
                                : t('COLLAPSE_RATE_CONDITIONS'),
                            hideRateConditionColumns ? (React.createElement(ChevronRightIcon, { className: "w-4 h-4" })) : (React.createElement(ChevronLeftIcon, { className: "w-4 h-4" }))),
                        React.createElement(DropdownMenu, { modal: false },
                            React.createElement(DropdownMenuTrigger, { asChild: true },
                                React.createElement(Button, { variant: "outline", className: "h-10" },
                                    t('ACTIONS'),
                                    " ",
                                    React.createElement(ChevronDownIcon, { className: "ml-2 w-4 h-4" }))),
                            React.createElement(DropdownMenuContent, { align: "end" },
                                React.createElement(DropdownMenuSeparator, null),
                                React.createElement(DropdownMenuItem, { onClick: () => handleBulkUploadClick() }, t('UPLOAD_LANE_RATES')),
                                React.createElement(DropdownMenuItem, null,
                                    React.createElement(Link, { to: "/lane-rates/add", className: "text-black" }, t('ADD_NEW_LANE_RATE'))),
                                React.createElement(DropdownMenuItem, null, t('EXPORT_LANE_RATES'))))))),
            selectedRowCount > 0 && (React.createElement(Alert, { className: "mt-4 text-sm bg-blue-50" },
                React.createElement("div", { className: "flex justify-between items-center" },
                    React.createElement("span", null,
                        selectedRowCount,
                        " ",
                        t('ROWS_SELECTED'),
                        "."),
                    React.createElement(Button, { variant: "link", onClick: () => onBulkEditRateSequence() },
                        t('EDIT_LANE_RATES'),
                        React.createElement(ArrowRightIcon, { className: "ml-2 w-4 h-4" })))))));
    }
    return (React.createElement("div", { className: "px-4 py-5 sm:p-6 mx-auto" },
        React.createElement(Card, null,
            renderHeader(),
            React.createElement(CardContent, { className: "space-y-4" },
                React.createElement(DataTable, { table: table, isLoading: isLoading, highlighted: highlighted }),
                renderFooter())),
        React.createElement(AuditTrailModal, { show: showAuditTrailModal, rate: rateAudit, onHide: () => setShowAuditTrailModal(false), type: "laneRates" }),
        showLaneRatesCsvUploadModal && (React.createElement(LaneRatesCsvUploadModal, { onClose: () => setLaneRatesCsvUploadModalIsOpen(false) })),
        visible && (React.createElement(EditRateSequenceModal, { laneRate: selectedRateSequence, open: visible, onOpenChange: close }))));
}
